<script setup lang="ts">
import { computed } from "vue"
import { DateTime } from "luxon"
import type { EditSessionFragment } from "@/graphql/types"
import type { EditSessionMode } from "../composables/useEditSession"

const props = defineProps<{
  mode: EditSessionMode
  others: EditSessionFragment[]
}>()
const emit = defineEmits<{ "take-over": [] }>()

const visible = computed(() => props.others.length > 0 && (props.mode === "viewing" || props.mode === "editing"))

function name(s: EditSessionFragment): string {
  return s.own ? "Du selbst (anderer Tab)" : s.displayName
}

function since(s: EditSessionFragment): string {
  const minutes = Math.floor(-DateTime.fromISO(String(s.startedAt)).diffNow("minutes").minutes)
  return minutes < 1 ? "gerade eben" : `seit ${minutes} Min.`
}

// Zeitangabe wird mit jedem Herzschlag/jeder Abfrage neu berechnet (others wird dann ersetzt).
const lines = computed(() =>
  props.others.map((s) => ({
    key: s.sessionId,
    text: `${name(s)} bearbeitet ${props.mode === "editing" ? "ebenfalls " : ""}(${since(s)})`,
    dirty: s.dirty,
  })),
)
</script>

<template>
  <div
    v-if="visible"
    :role="mode === 'viewing' ? 'alert' : 'status'"
    class="alert text-sm mb-4"
    :class="mode === 'viewing' ? 'alert-warning' : 'alert-info'"
    data-testid="edit-session-banner"
  >
    <div class="space-y-1">
      <p v-for="line in lines" :key="line.key">
        {{ line.text }}<span v-if="line.dirty"> · ungespeicherte Änderungen</span>
      </p>
      <p v-if="mode === 'viewing'" class="opacity-80">Die Maske ist schreibgeschützt, damit ihr euch nicht in die Quere kommt.</p>
    </div>
    <button v-if="mode === 'viewing'" type="button" class="btn btn-sm" @click="emit('take-over')">
      Trotzdem bearbeiten
    </button>
  </div>
</template>
